import { z } from "zod";
import {
  DATA_COMPLETENESS_STATUSES,
  DRIVER_TAGS,
  DRIVER_TYPES,
  KB_BLOCK_IDS,
  OUTCOME_INSIGHT_TAGS,
  PROJECT_LIFECYCLE_PHASES,
  RECOVERABILITY_STATUSES,
} from "./enums";

export const projectLifecyclePhaseSchema = z.enum(PROJECT_LIFECYCLE_PHASES);
export const recoverabilityStatusSchema = z.enum(RECOVERABILITY_STATUSES);
export const dataCompletenessStatusSchema = z.enum(DATA_COMPLETENESS_STATUSES);
export const driverTypeSchema = z.enum(DRIVER_TYPES);
export const kbBlockIdSchema = z.enum(KB_BLOCK_IDS);
export const driverTagSchema = z.enum(DRIVER_TAGS);
export const outcomeInsightTagSchema = z.enum(OUTCOME_INSIGHT_TAGS);

export type ProjectLifecyclePhase = z.output<typeof projectLifecyclePhaseSchema>;
export type RecoverabilityStatus = z.output<typeof recoverabilityStatusSchema>;
export type DataCompletenessStatus = z.output<typeof dataCompletenessStatusSchema>;
export type DriverType = z.output<typeof driverTypeSchema>;
export type KBBlockId = z.output<typeof kbBlockIdSchema>;
export type DriverTag = z.output<typeof driverTagSchema>;
export type OutcomeInsightTag = z.output<typeof outcomeInsightTagSchema>;

export const documentRefSchema = z
  .object({
    projectFileId: z.string().min(1).nullable(),
    filePath: z.string().min(1).nullable(),
    title: z.string().min(1),
    docType: z.string().min(1).nullable(),
    snippet: z.string().min(1).nullable(),
  })
  .strict();

export type DocumentRef = z.output<typeof documentRefSchema>;

export const knowledgeQualityItemSchema = z
  .object({
    key: z.string().min(1),
    label: z.string().min(1),
    status: dataCompletenessStatusSchema,
    affectsBlocks: z.array(kbBlockIdSchema).min(1),
  })
  .strict();

export const outcomeStatusAmountSchema = z
  .object({
    status: recoverabilityStatusSchema,
    amountSek: z.number().finite().nullable(),
    percentOfAta: z.number().finite().nullable(),
  })
  .strict();

export const outcomeDriverRefSchema = z
  .object({
    driverId: z.string().min(1),
    amountSek: z.number().finite().nullable(),
    hours: z.number().finite().nullable(),
    days: z.number().finite().nullable(),
    tags: z.array(outcomeInsightTagSchema),
  })
  .strict();

export const driverItemSchema = z
  .object({
    id: z.string().min(1),
    rank: z.number().int().positive(),
    title: z.string().min(1),
    description: z.string().min(1),
    type: driverTypeSchema,
    impact: z
      .object({
        amountSek: z.number().finite().nullable(),
        hours: z.number().finite().nullable(),
        days: z.number().finite().nullable(),
      })
      .strict(),
    originPhase: projectLifecyclePhaseSchema,
    discoveryPhase: projectLifecyclePhaseSchema,
    recoverabilityStatus: recoverabilityStatusSchema,
    tags: z.array(driverTagSchema),
    sourceCount: z.number().int().nonnegative(),
    whyItHappened: z.array(z.string().min(1)),
    compensation: z
      .object({
        blockers: z.array(z.string().min(1)),
        requiredForRecovery: z.string().min(1).nullable(),
      })
      .strict()
      .nullable(),
    evidence: z.array(documentRefSchema).min(1),
  })
  .strict();

export const earlyWarningItemSchema = z
  .object({
    id: z.string().min(1),
    linkedDriverIds: z.array(z.string().min(1)).min(1),
    title: z.string().min(1),
    signalText: z.string().min(1),
    earliestDetectablePhase: projectLifecyclePhaseSchema,
    discoveredInPhase: projectLifecyclePhaseSchema,
    possibleSavingsSek: z.number().finite().nullable(),
    possibleSavingsHours: z.number().finite().nullable(),
    possibleSavingsDays: z.number().finite().nullable(),
    evidence: z.array(documentRefSchema).min(1),
  })
  .strict();

export const playbookGroupSchema = z
  .object({
    phase: projectLifecyclePhaseSchema,
    items: z
      .array(
        z
          .object({
            id: z.string().min(1),
            actionText: z.string().min(1),
            ownerRole: z.string().min(1),
            linkedDriverIds: z.array(z.string().min(1)).min(1),
            evidence: z.array(documentRefSchema).min(1),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

const lifecycleOrderedPhasesSchema = z
  .tuple([
    z.literal("tender_and_planning"),
    z.literal("design"),
    z.literal("production"),
    z.literal("final_inspection"),
  ])
  .transform((phases) => [...phases]);

export const historicalProjectViewModelSchema = z
  .object({
    schemaVersion: z.literal(1),
    locale: z.literal("sv-SE"),
    currency: z.literal("SEK"),
    project: z
      .object({
        id: z.string().uuid(),
        orgId: z.string().uuid(),
        mode: z.literal("historical"),
        name: z.string().min(1),
        summaryShort: z.string().min(1),
        categoryLabel: z.string().min(1),
        locationLabel: z.string().min(1),
        year: z.number().int().nullable(),
      })
      .strict(),
    projectBrief: z
      .object({
        narrative: z.string().min(1),
        contractForm: z.string().min(1).nullable(),
        contractValueSek: z.number().finite().nullable(),
        plannedDurationDays: z.number().finite().nullable(),
        actualDurationDays: z.number().finite().nullable(),
        disciplineLabel: z.string().min(1).nullable(),
        clientLabel: z.string().min(1).nullable(),
      })
      .strict(),
    lifecycle: z
      .object({
        orderedPhases: lifecycleOrderedPhasesSchema,
      })
      .strict(),
    knowledgeQuality: z
      .object({
        totalSources: z.number().int().nonnegative(),
        completeCount: z.number().int().nonnegative(),
        partialCount: z.number().int().nonnegative(),
        missingCount: z.number().int().nonnegative(),
        items: z.array(knowledgeQualityItemSchema).min(1),
      })
      .strict(),
    outcome: z
      .object({
        money: z
          .object({
            ataTotalSek: z.number().finite().nullable(),
            ataShareOfContractPct: z.number().finite().nullable(),
            statusBreakdown: z.array(outcomeStatusAmountSchema),
            topDriverRefs: z.array(outcomeDriverRefSchema),
          })
          .strict(),
        time: z
          .object({
            unplannedDaysTotal: z.number().finite().nullable(),
            extraMobilizations: z.number().int().nonnegative().nullable(),
            grantedExtensionDays: z.number().finite().nullable(),
            delayPenaltyText: z.string().min(1).nullable(),
            topDriverRefs: z.array(outcomeDriverRefSchema),
          })
          .strict(),
      })
      .strict(),
    drivers: z.array(driverItemSchema).min(1),
    earlyWarnings: z.array(earlyWarningItemSchema).min(1),
    playbook: z
      .object({
        groups: z.array(playbookGroupSchema).min(1),
      })
      .strict(),
  })
  .strict()
  .superRefine((model, ctx) => {
    const uniqueDriverIds = new Set(model.drivers.map((driver) => driver.id));
    const duplicateDriverIds = model.drivers.length - uniqueDriverIds.size;

    if (duplicateDriverIds > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Driver IDs must be unique.",
        path: ["drivers"],
      });
    }

    for (const warning of model.earlyWarnings) {
      for (const linkedDriverId of warning.linkedDriverIds) {
        if (!uniqueDriverIds.has(linkedDriverId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Early warning ${warning.id} links to missing driver ID ${linkedDriverId}.`,
            path: ["earlyWarnings"],
          });
        }
      }
    }

    for (const group of model.playbook.groups) {
      for (const item of group.items) {
        for (const linkedDriverId of item.linkedDriverIds) {
          if (!uniqueDriverIds.has(linkedDriverId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Playbook item ${item.id} links to missing driver ID ${linkedDriverId}.`,
              path: ["playbook", "groups"],
            });
          }
        }
      }
    }
  });

export type HistoricalProjectViewModel = z.output<typeof historicalProjectViewModelSchema>;
export type HistoricalProjectViewModelInput = z.input<typeof historicalProjectViewModelSchema>;
